import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import type {FC} from 'react';
import {useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {Switch} from 'src/design/atoms/Switch';
import {Textbox} from 'src/design/atoms/Textbox';
import {AssistantUsageMeter} from './AssistantUsageMeter';

export interface ChatComposerProps {
  disabled?: boolean;
  isStreaming: boolean;
  onSend: (content: string, options: {thinkHarder: boolean}) => void;
  onStop: () => void;
}

export const ChatComposer: FC<ChatComposerProps> = ({
  disabled,
  isStreaming,
  onSend,
  onStop,
}) => {
  const [value, setValue] = useState('');
  const [thinkHarder, setThinkHarder] = useState(false);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || isStreaming || disabled) return;
    onSend(trimmed, {thinkHarder});
    setValue('');
    setThinkHarder(false);
  };

  return (
    <Stack gap={4} p="sm" style={{flexShrink: 0}}>
      <Textbox
        ariaLabel="Message Scout"
        placeholder="Ask about Proact or your organization’s procedures..."
        helperText="Enter to send · Shift+Enter for a new line"
        value={value}
        multiline
        minRows={2}
        maxRows={8}
        disabled={disabled}
        onChangeCallback={event => setValue(event.currentTarget.value)}
        onEnterCallback={event => {
          if (
            !event.shiftKey &&
            !event.nativeEvent.isComposing &&
            event.keyCode !== 229
          ) {
            event.preventDefault();
            handleSend();
          }
        }}
      />
      <Group wrap="nowrap">
        {/* Flex spacer keeps the button right-aligned when the meter renders nothing. */}
        <Box style={{flex: 1, minWidth: 0, display: 'flex'}}>
          <AssistantUsageMeter />
        </Box>
        <Switch
          label="Think harder"
          size="sm"
          checked={thinkHarder}
          disabled={isStreaming || disabled}
          onChange={event => setThinkHarder(event.currentTarget.checked)}
          style={{flexShrink: 0}}
        />
        {isStreaming ? (
          <Button color="red" onClick={onStop} miw={96}>
            Stop
          </Button>
        ) : (
          <Button
            onClick={handleSend}
            disabled={disabled || !value.trim()}
            miw={96}>
            Send
          </Button>
        )}
      </Group>
    </Stack>
  );
};
